import React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import { EmptyState, MeterBar, Modal, Panel, StatusChip, ToolbarButton } from '../../../ui-kit';
import { fetchCheck, openDocument, type PcCheckDetail, type PcFinding } from '../../../services/policyCheckingService';
import { useLive, when } from '../rocco/live';
import { SEVERITY, money, resultChip, sizeOf } from './status';

/**
 * One policy, checked: the documents that were read, then every field the
 * agent looked at, side by side across the quote, the application, the issued
 * policy and our own records.
 *
 * The grid is the point. A finding on its own is a claim; a finding with the
 * three values beside each other is something a person can settle in a glance
 * and then go and look up on the page itself — which is why each document can
 * be opened from here. Watches the check while open, so a document still
 * being read appears as it lands.
 */
export default function CheckStoryModal({ checkId, onClose }: { checkId: string; onClose: () => void }) {
  const fetcher = React.useCallback(() => fetchCheck(checkId), [checkId]);
  // Quick, because this window is where the reading is watched happening.
  const { data: check, error } = useLive(fetcher, 2500);

  const subtitle = check ? [
    check.insured_name, check.carrier, check.product,
    check.face_amount != null && money(check.face_amount),
    `started ${when(check.created_at)}`,
  ].filter(Boolean).join(' · ') : undefined;

  return (
    <Modal
      open
      onClose={onClose}
      title={check ? (check.policy_num
        || check.insured_name
        || (check.status === 'reading' ? 'Checking a policy' : 'Policy check')) : 'Loading…'}
      subtitle={subtitle}
      actions={check ? <StatusChip {...resultChip(check)} /> : undefined}
      size="lg"
      footer={<ToolbarButton variant="ghost" onClick={onClose}>Close</ToolbarButton>}
    >
      {error && !check ? <EmptyState message={error} /> : null}
      {!check && !error ? <Loading /> : null}
      {check ? (
        <Stack spacing={2}>
          <Summary check={check} />
          <Documents check={check} />
          {check.status === 'checked' ? <Grid check={check} /> : null}
        </Stack>
      ) : null}
    </Modal>
  );
}

/** What the check came to, in one line, before the detail. */
function Summary({ check }: { check: PcCheckDetail }) {
  if (check.status === 'reading') {
    const done = check.documents.filter((d) => d.status !== 'uploaded').length;
    const total = Math.max(1, check.documents.length);
    const reading = check.documents.find((d) => d.status === 'uploaded');
    return (
      <Box sx={(t) => ({ p: 1.5, borderRadius: `${t.ds.radius.sm}px`, bgcolor: t.tokens.surface.sunken })}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <StatusChip tone="good" label="Working" pulse />
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
              {done === check.documents.length
                ? 'Lining the documents up and checking them field by field…'
                : `Reading ${reading ? reading.filename : 'the documents'}…`}
            </Typography>
          </Stack>
          <MeterBar value={(done / total) * 100} readout={`${done} of ${check.documents.length} read`} tone="brand" />
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            Each document is read on its own, then compared against the others. This window follows
            along, so there is nothing to do but watch.
          </Typography>
        </Stack>
      </Box>
    );
  }
  if (check.status === 'failed') {
    return <Note tone="bad">{check.error || 'None of the documents could be read.'}</Note>;
  }
  const looked = check.must_fix + check.review + check.matched;
  if (check.result === 'all_clear') {
    return <Note tone="good">Everything the agent could check agrees — {looked} fields across the documents.</Note>;
  }
  const parts = [
    check.must_fix ? `${check.must_fix} to fix` : null,
    check.review ? `${check.review} to review` : null,
  ].filter(Boolean).join(' and ');
  return (
    <Note tone={check.must_fix ? 'bad' : 'warning'}>
      {parts} out of {looked} fields checked. The rest agree.
      {check.missing.length ? ` No ${check.missing.join(' or ')} was uploaded, so anything only that document states could not be checked.` : ''}
    </Note>
  );
}

function Note({ tone, children }: { tone: 'good' | 'bad' | 'warning' | 'neutral'; children: React.ReactNode }) {
  return (
    <Box sx={(t) => ({
      p: 1.25,
      borderRadius: `${t.ds.radius.sm}px`,
      bgcolor: {
        good: t.tokens.semantic.successSoft, bad: t.tokens.semantic.dangerSoft,
        warning: t.tokens.semantic.warningSoft, neutral: t.tokens.surface.sunken,
      }[tone],
      color: t.tokens.text.primary,
    })}>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

/** The files, what each was taken to be, and a way to open it. */
function Documents({ check }: { check: PcCheckDetail }) {
  const [problem, setProblem] = React.useState<string | null>(null);
  const open = async (id: string) => {
    setProblem(null);
    try {
      await openDocument(id);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not open that document');
    }
  };
  return (
    <Panel title="The documents" subtitle={problem || 'What each one was taken to be, read from the page itself'}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}>
        {check.documents.map((document) => (
          <Stack
            key={document.id}
            spacing={0.75}
            sx={(t) => ({
              p: 1.25,
              minWidth: 0,
              borderRadius: `${t.ds.radius.md}px`,
              border: `1px solid ${document.status === 'failed' ? t.tokens.semantic.danger : t.tokens.border.subtle}`,
              bgcolor: t.tokens.surface.raised,
            })}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <DescriptionRounded sx={(t) => ({ fontSize: 18, color: t.tokens.text.tertiary })} />
              <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>
                {document.status === 'uploaded' ? 'Reading'
                  : document.status === 'failed' ? 'Could not be read'
                    : document.kind === 'unknown' ? 'Not recognised' : document.kind_label}
              </Typography>
              {document.status === 'uploaded' ? <StatusChip tone="neutral" label="Working on it" pulse /> : null}
            </Stack>
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
              {document.filename} · {sizeOf(document.size_bytes)}
              {document.pages ? ` · ${document.pages} page${document.pages === 1 ? '' : 's'}` : ''}
            </Typography>
            {document.error ? (
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{document.error}</Typography>
            ) : document.status === 'uploaded' ? (
              <Skeleton variant="text" width="80%" aria-hidden />
            ) : document.summary ? (
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary, overflowWrap: 'anywhere' })}>
                {document.summary}
              </Typography>
            ) : null}
            <Box>
              <ToolbarButton variant="ghost" icon={<OpenInNewRounded />} onClick={() => open(document.id)}>
                Open
              </ToolbarButton>
            </Box>
          </Stack>
        ))}
      </Box>
    </Panel>
  );
}

/** Every field the agent looked at, the documents side by side. */
function Grid({ check }: { check: PcCheckDetail }) {
  // Everything the agent looked at, worst first — the check should say what it
  // checked, not only what it caught, and nobody should have to ask for that.
  const flagged = check.findings.filter((f) => f.severity !== 'match');
  const matches = check.findings.length - flagged.length;
  const columns = check.columns;
  const template = { xs: '1fr', md: `minmax(150px, 1.1fr) repeat(${columns.length}, minmax(110px, 1fr)) 110px` };

  return (
    <Panel
      title="Field by field"
      subtitle={`${flagged.length} flagged · ${matches} agree · across ${columns.map((c) => c.label).join(', ')}`}
    >
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ minWidth: { md: 140 * (columns.length + 2) } }}>
          {/* Headings: the documents, in the order the work happens. */}
          <Box sx={(t) => ({
            display: { xs: 'none', md: 'grid' },
            gridTemplateColumns: template,
            columnGap: 1.5,
            pb: 0.75,
            borderBottom: `1px solid ${t.tokens.border.subtle}`,
          })}>
            <Typography variant="overline" sx={(t) => ({ color: t.tokens.text.tertiary })}>Field</Typography>
            {columns.map((column) => (
              <Typography key={column.key} variant="overline" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                {column.label}
              </Typography>
            ))}
            <Typography variant="overline" sx={(t) => ({ color: t.tokens.text.tertiary })}>Result</Typography>
          </Box>

          {check.findings.map((finding, index) => (
            <Row key={`${finding.label}-${index}`} finding={finding} columns={columns} template={template} />
          ))}
        </Box>
      </Box>
    </Panel>
  );
}

function Row({
  finding, columns, template,
}: { finding: PcFinding; columns: { key: string; label: string }[]; template: Record<string, string> }) {
  const flagged = finding.severity !== 'match';
  return (
    <Box
      sx={(t) => ({
        display: 'grid',
        gridTemplateColumns: template,
        columnGap: 1.5,
        rowGap: 0.25,
        alignItems: 'baseline',
        py: 1,
        borderBottom: `1px solid ${t.tokens.border.subtle}`,
        '&:last-of-type': { borderBottom: 'none' },
      })}
    >
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: flagged ? 600 : 400 })}>
          {finding.label}
        </Typography>
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{finding.area}</Typography>
      </Stack>

      {columns.map((column) => {
        const value = finding.values[column.key];
        return (
          <Box key={column.key} sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={(t) => ({ color: t.tokens.text.tertiary, display: { xs: 'block', md: 'none' } })}
            >
              {column.label}
            </Typography>
            <Typography
              variant="body2"
              sx={(t) => ({
                overflowWrap: 'anywhere',
                color: value == null ? t.tokens.text.tertiary
                  : flagged ? (finding.severity === 'must_fix' ? t.tokens.semantic.danger : t.tokens.semantic.warning)
                    : t.tokens.text.primary,
                fontWeight: flagged && value != null ? 600 : 400,
              })}
            >
              {value ?? '—'}
            </Typography>
          </Box>
        );
      })}

      <Box>
        <StatusChip {...SEVERITY[finding.severity]} />
      </Box>

      {finding.detail ? (
        <Typography
          variant="caption"
          sx={(t) => ({ color: t.tokens.text.secondary, gridColumn: { md: '1 / -1' }, pt: 0.25 })}
        >
          {finding.detail}
        </Typography>
      ) : null}
    </Box>
  );
}

function Loading() {
  return (
    <Stack spacing={1.5} aria-hidden>
      <Skeleton variant="rounded" height={48} />
      <Skeleton variant="rounded" height={140} />
      <Skeleton variant="rounded" height={260} />
    </Stack>
  );
}
